import { useState } from 'react';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { RefreshCw, Loader2, AlertTriangle, Baby, GraduationCap } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../lib/auth-context';

// V14.5 Part 17c — Migrations page (renamed from Data & Content).
// Wise.live sunset 11 May 2026: removed Wise sync card, Auto-pull-suspended,
// Suspend-by-Wise-list, Smart Clean, and the Wise-counter stats. Page now
// hosts only the two active one-shot migrations: MTTF→Postnatal and
// Wise→LMS Garbhsanskar. Reduces admin JS bundle by ~340 lines + 4 API calls
// on page load (which were hitting deprecated /api/wise-sync/* endpoints).
export default function DataMigration() {
  const { user } = useAuth();
  const [mttfBusy, setMttfBusy] = useState(false);
  const [mttfPreview, setMttfPreview] = useState(null);
  const [mttfApplied, setMttfApplied] = useState(null);
  const [garbhBusy, setGarbhBusy] = useState(false);
  const [garbhPreview, setGarbhPreview] = useState(null);
  const [garbhApplied, setGarbhApplied] = useState(null);
  const [garbhCreateMissing, setGarbhCreateMissing] = useState(false);

  const previewMttf = async () => {
    setMttfBusy(true);
    setMttfApplied(null);
    try {
      const res = await api.post('/api/admin/migrations/mttf-to-postnatal', { dry_run: true });
      setMttfPreview(res.data);
      const w = res.data.warning;
      toast[w ? 'message' : 'success'](
        w || `${res.data.matched_batches.length} batch(es) — ${res.data.learners_marked_postnatal} would be flipped, ${res.data.learners_pending_onboarding} need onboarding`
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Preview failed');
    } finally {
      setMttfBusy(false);
    }
  };

  const applyMttf = async () => {
    if (!mttfPreview || mttfPreview.matched_batches.length === 0) { toast.error('Run Preview first'); return; }
    if (!window.confirm(`Convert ${mttfPreview.matched_batches.length} batch(es) and ${mttfPreview.learners_total} learner(s) to postnatal? Learners without a delivery date will be prompted to enter it on next login. Idempotent — safe to re-run.`)) return;
    setMttfBusy(true);
    try {
      const res = await api.post('/api/admin/migrations/mttf-to-postnatal', { dry_run: false });
      setMttfApplied(res.data);
      toast.success(`Done — ${res.data.batches_updated} batches updated, ${res.data.learners_marked_postnatal} learners flipped, ${res.data.learners_pending_onboarding} will see the onboarding modal`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Apply failed');
    } finally {
      setMttfBusy(false);
    }
  };

  const previewGarbh = async () => {
    setGarbhBusy(true);
    setGarbhApplied(null);
    try {
      const res = await api.post('/api/admin/migrations/wise-garbhsanskar-learners', { dry_run: true });
      setGarbhPreview(res.data);
      const s = res.data.summary;
      toast.success(`${s.total_wise_enrolled} on Wise — ${s.granted + s.already_active} match LMS users · ${s.unmatched} unmatched`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Preview failed');
    } finally {
      setGarbhBusy(false);
    }
  };

  const applyGarbh = async () => {
    if (!garbhPreview) { toast.error('Run Preview first'); return; }
    const s = garbhPreview.summary;
    const confirmMsg = garbhCreateMissing
      ? `Grant entitlement to ${s.total_wise_enrolled} learner(s): ${s.granted + s.already_active} matched LMS users AND auto-create stub accounts for ${s.unmatched} unmatched (so the course waits for them on first WhatsApp/Google login). Idempotent.`
      : `Grant entitlement to ${s.granted + s.already_active} matched LMS user(s). ${s.unmatched} unmatched learner(s) will be logged for follow-up but NOT granted. Idempotent.`;
    if (!window.confirm(confirmMsg)) return;
    setGarbhBusy(true);
    try {
      const res = await api.post('/api/admin/migrations/wise-garbhsanskar-learners', {
        dry_run: false,
        create_missing: garbhCreateMissing,
      });
      setGarbhApplied(res.data);
      const r = res.data.summary;
      toast.success(`Done — granted ${r.granted}, already active ${r.already_active}${garbhCreateMissing ? `, created ${r.created_stubs} stubs` : ''}, ${r.unmatched} still unmatched`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Apply failed');
    } finally {
      setGarbhBusy(false);
    }
  };

  if (user?.role !== 'superadmin') return <div className="text-center py-12"><p className="text-muted-foreground">SuperAdmin access only</p></div>;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-heading text-2xl font-semibold">Migrations</h2>
        <p className="text-muted-foreground mt-1">One-shot data migrations. All operations are idempotent — safe to re-run.</p>
      </div>

      {/* MTTF → Postnatal one-time migration */}
      <Card className="rounded-2xl border-rose-200 bg-rose-50/30">
        <CardHeader>
          <CardTitle className="font-heading text-lg flex items-center gap-2 text-rose-900">
            <Baby className="h-5 w-5" />Convert MTTF Batches → Postnatal
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-muted-foreground">
            Auto-detects every batch whose name contains <code>MTTF</code> and <code>Postnatal</code>, flips
            <code> batch.course_type</code> → <code>postnatal</code>, and migrates each enrolled learner's stage to postnatal.
            Learners without a recorded delivery date will see a <strong>mandatory onboarding modal</strong> on
            next login asking for delivery date + delivery mode (Natural / C-section). Idempotent.
          </p>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              onClick={previewMttf}
              disabled={mttfBusy}
              data-testid="mttf-preview-btn"
            >
              {mttfBusy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
              {mttfPreview ? 'Re-run Preview' : 'Preview MTTF Conversion'}
            </Button>
            {mttfPreview && mttfPreview.matched_batches.length > 0 && (
              <Button
                variant="destructive"
                onClick={applyMttf}
                disabled={mttfBusy || !!mttfApplied}
                data-testid="mttf-apply-btn"
              >
                {mttfBusy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Baby className="h-4 w-4 mr-2" />}
                Convert {mttfPreview.learners_total} learner(s) to Postnatal
              </Button>
            )}
          </div>

          {mttfPreview && (
            <div className="text-xs space-y-2 p-3 rounded-md bg-background border" data-testid="mttf-preview-result">
              {mttfPreview.warning ? (
                <div className="flex items-start gap-2 text-amber-800">
                  <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                  <span>{mttfPreview.warning}</span>
                </div>
              ) : (
                <>
                  <div className="flex flex-wrap gap-x-4 gap-y-1">
                    <span><strong>Batches matched:</strong> {mttfPreview.matched_batches.length}</span>
                    <span><strong>Total learners:</strong> {mttfPreview.learners_total}</span>
                    <span className="text-emerald-700"><strong>Will flip to postnatal:</strong> {mttfPreview.learners_marked_postnatal}</span>
                    <span className="text-amber-700"><strong>Already postnatal:</strong> {mttfPreview.learners_already_postnatal}</span>
                    <span className="text-blue-700"><strong>Will see onboarding modal:</strong> {mttfPreview.learners_pending_onboarding}</span>
                    <span className="text-muted-foreground"><strong>Already have delivery date:</strong> {mttfPreview.learners_with_existing_delivery_date}</span>
                  </div>
                  <details>
                    <summary className="cursor-pointer text-rose-800 font-medium">Matched batches ({mttfPreview.matched_batches.length})</summary>
                    <ul className="mt-1 ml-4 list-disc">
                      {mttfPreview.matched_batches.map((b, i) => (
                        <li key={i} className="font-mono">
                          {b.name} <span className="text-muted-foreground">(course_type: {b.course_type_before || 'unset'} → postnatal · {b.learner_count} learners)</span>
                        </li>
                      ))}
                    </ul>
                  </details>
                  {mttfPreview.preview?.pending_onboarding?.length > 0 && (
                    <details>
                      <summary className="cursor-pointer text-blue-700 font-medium">Will see onboarding modal ({mttfPreview.preview.pending_onboarding.length}{mttfPreview.learners_pending_onboarding > 50 ? `, showing first 50` : ''})</summary>
                      <ul className="mt-1 ml-4 list-disc max-h-64 overflow-y-auto">
                        {mttfPreview.preview.pending_onboarding.map((u, i) => (
                          <li key={i} className="font-mono">{u.name} &lt;{u.email}&gt;</li>
                        ))}
                      </ul>
                    </details>
                  )}
                </>
              )}
            </div>
          )}

          {mttfApplied && (
            <div className="text-xs p-3 rounded-md bg-emerald-100 border border-emerald-300 text-emerald-900" data-testid="mttf-applied">
              ✅ <strong>Migration done.</strong> {mttfApplied.batches_updated} batch(es) flipped, {mttfApplied.learners_marked_postnatal} learner(s) moved to postnatal,
              {' '}{mttfApplied.learners_pending_onboarding} will see the modal on next login,
              {' '}{mttfApplied.learners_already_postnatal} were already postnatal.
            </div>
          )}
        </CardContent>
      </Card>

      {/* Wise → LMS Garbhsanskar migration */}
      <Card className="rounded-2xl border-indigo-200 bg-indigo-50/30">
        <CardHeader>
          <CardTitle className="font-heading text-lg flex items-center gap-2 text-indigo-900">
            <GraduationCap className="h-5 w-5" />Wise → LMS: Modern Garbhsanskar Program
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-muted-foreground">
            Pulls every learner enrolled in the legacy Wise.live <strong>"Modern Garbhsanskar Program"</strong> recorded
            course and grants them full entitlement to the LMS <strong>"Modern Garbhsanskar Masterclass"</strong> course
            (<code>ops_sku=garbhsanskar_masterclass</code>). Match priority: email (case-insensitive) → phone (last-10
            digit suffix). Unmatched learners are logged to <code>wise_migration_unmatched_col</code> for follow-up.
            Idempotent — re-runnable safely.
          </p>

          <label className="flex items-center gap-2 text-xs cursor-pointer select-none" data-testid="garbh-create-missing-toggle">
            <input
              type="checkbox"
              checked={garbhCreateMissing}
              onChange={(e) => setGarbhCreateMissing(e.target.checked)}
              className="rounded border-indigo-300"
            />
            <span>
              <strong>Auto-create stub LMS accounts</strong> for unmatched learners so the course waits for them on
              their first WhatsApp OTP / Google login (auto-merge by phone or email).
            </span>
          </label>

          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              onClick={previewGarbh}
              disabled={garbhBusy}
              data-testid="garbh-preview-btn"
            >
              {garbhBusy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
              {garbhPreview ? 'Re-run Preview' : 'Preview Wise → LMS Migration'}
            </Button>
            {garbhPreview && garbhPreview.summary.total_wise_enrolled > 0 && (
              <Button
                onClick={applyGarbh}
                disabled={garbhBusy || !!garbhApplied}
                data-testid="garbh-apply-btn"
                className="bg-indigo-700 hover:bg-indigo-800"
              >
                {garbhBusy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <GraduationCap className="h-4 w-4 mr-2" />}
                {garbhCreateMissing
                  ? `Grant + create stubs for ${garbhPreview.summary.total_wise_enrolled} learner(s)`
                  : `Grant entitlement to ${garbhPreview.summary.granted + garbhPreview.summary.already_active} matched learner(s)`}
              </Button>
            )}
          </div>

          {garbhPreview && (
            <div className="text-xs space-y-2 p-3 rounded-md bg-background border" data-testid="garbh-preview-result">
              <div className="flex flex-wrap gap-x-4 gap-y-1">
                <span><strong>Wise enrolled:</strong> {garbhPreview.summary.total_wise_enrolled}</span>
                <span className="text-emerald-700"><strong>{garbhPreview.dry_run ? 'Would grant' : 'Granted'}:</strong> {garbhPreview.summary.granted}</span>
                <span className="text-amber-700"><strong>Already active:</strong> {garbhPreview.summary.already_active}</span>
                {garbhPreview.summary.created_stubs > 0 && (
                  <span className="text-indigo-700"><strong>Stubs created:</strong> {garbhPreview.summary.created_stubs}</span>
                )}
                <span className="text-rose-700"><strong>Unmatched:</strong> {garbhPreview.summary.unmatched}</span>
                {garbhPreview.summary.errored > 0 && (
                  <span className="text-rose-700"><strong>Errors:</strong> {garbhPreview.summary.errored}</span>
                )}
              </div>
              {garbhPreview.samples?.unmatched?.length > 0 && (
                <details>
                  <summary className="cursor-pointer text-rose-800 font-medium">
                    Sample unmatched ({Math.min(10, garbhPreview.summary.unmatched)} of {garbhPreview.summary.unmatched})
                  </summary>
                  <ul className="mt-1 ml-4 list-disc max-h-48 overflow-y-auto">
                    {garbhPreview.samples.unmatched.map((u, i) => (
                      <li key={i} className="font-mono">{u.name} &lt;{u.email}&gt; ({u.phone})</li>
                    ))}
                  </ul>
                </details>
              )}
              {garbhPreview.samples?.granted?.length > 0 && (
                <details>
                  <summary className="cursor-pointer text-emerald-700 font-medium">
                    Sample {garbhPreview.dry_run ? 'would-grant' : 'granted'} ({Math.min(10, garbhPreview.summary.granted)} of {garbhPreview.summary.granted})
                  </summary>
                  <ul className="mt-1 ml-4 list-disc max-h-48 overflow-y-auto">
                    {garbhPreview.samples.granted.map((u, i) => (
                      <li key={i} className="font-mono">&lt;{u.email}&gt;</li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}

          {garbhApplied && (
            <div className="text-xs p-3 rounded-md bg-emerald-100 border border-emerald-300 text-emerald-900" data-testid="garbh-applied">
              ✅ <strong>Migration done.</strong>
              {' '}Granted {garbhApplied.summary.granted}, already active {garbhApplied.summary.already_active}
              {garbhApplied.summary.created_stubs > 0 && <>, created {garbhApplied.summary.created_stubs} stub account(s)</>}
              , {garbhApplied.summary.unmatched} still unmatched (logged for follow-up).
            </div>
          )}
        </CardContent>
      </Card>

      <div className="text-[11px] text-muted-foreground text-center py-2 border-t pt-4">
        Wise.live sync was retired on <strong>11 May 2026</strong>. Legacy bulk-import and Smart-Clean flows were removed.
        Bulk-suspend is available in the <strong>Users</strong> tab.
      </div>
    </div>
  );
}
